import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Phone,
  Calendar,
  ChevronRight,
  AlertTriangle,
  Loader,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { assessmentData } from '../../data/assessments';
import { useApi } from '../../hooks/useApi';
import UrgentBookingModal from '../../components/assessment/UrgentBookingModal';

const AssessmentPage = () => {
  const { assessmentType } = useParams();
  const navigate = useNavigate();

  const assessment = assessmentData[assessmentType];
  const { post } = useApi();

  // State management
  const [step, setStep] = useState('intro'); // 'intro', 'personal', 'questions', 'results'
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    age: '',
  });
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showUrgentBooking, setShowUrgentBooking] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);

  // Redirect if invalid assessment type
  if (!assessment) {
    navigate('/');
    return null;
  }

  const Icon = assessment.icon;
  const totalQuestions = assessment.questions.length;
  const progress = (currentQuestion / totalQuestions) * 100;

  // Handle personal info input
  const handlePersonalInfoChange = (e) => {
    setPersonalInfo({
      ...personalInfo,
      [e.target.name]: e.target.value,
    });
  };

  // Handle answer selection
  const handleAnswer = (questionId, optionScore) => {
    setAnswers({
      ...answers,
      [questionId]: optionScore,
    });
  };

  // Navigate to next question
  const handleNext = async () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate final score
      const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
      setScore(totalScore);

      // Submit to backend
      await submitAssessment(totalScore);
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Get result interpretation
  const getResultInterpretation = (scoreToUse) => {
    const ranges = assessment.scoreRanges;
    const actualScore = scoreToUse !== undefined ? scoreToUse : score;
    for (const range of ranges) {
      if (actualScore >= range.min && actualScore <= range.max) {
        return range;
      }
    }
    return ranges[0];
  };

  // Submit assessment to backend
  const submitAssessment = async (totalScore) => {
    setSubmitting(true);
    try {
      const result = getResultInterpretation(totalScore);

      const response = await post('/assessments/submit', {
        personalInfo,
        assessmentType: assessment.id,
        assessmentTitle: assessment.title,
        totalScore,
        maxScore: assessment.maxScore,
        riskLevel: result.level,
        riskColor: result.color,
        interpretation: result.interpretation,
        recommendations: result.recommendations
      });

      console.log('Assessment submission response:', response);
      console.log('Response data:', response?.data?.data);

      if (response?.data?.data) {
        const assessmentData = response.data.data;
        setAssessmentResult(assessmentData);

        // Show urgent booking modal for high-risk cases
        console.log('Checking high-risk status:', {
          isHighRisk: assessmentData.isHighRisk,
          isCritical: assessmentData.isCritical
        });

        if (assessmentData.isHighRisk || assessmentData.isCritical) {
          console.log('Setting showUrgentBooking to true');
          setShowUrgentBooking(true);
        }
      }

      setStep('results');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Still show results even if submission fails
      setStep('results');
    } finally {
      setSubmitting(false);
    }
  };

  // Intro Screen
  if (step === 'intro') {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/uzimabanner.jpg"
            alt="Mental health assessment and wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-uzima-maroon-900/75 via-uzima-maroon-800/70 to-uzima-white-800/75"></div>
        </div>

        <div className="relative z-10 min-h-screen py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/90 hover:text-white mb-6 font-medium transition-colors bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20"
            >
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-${assessment.color}-500 to-${assessment.color}-600 flex items-center justify-center mb-6`}>
              <Icon className="w-8 h-8 text-white" />
            </div>

            <h1 className="text-3xl font-display font-bold text-gray-900 mb-4">
              {assessment.title}
            </h1>

            <p className="text-lg text-gray-600 mb-6">
              {assessment.description}
            </p>

            <div className="bg-uzima-maroon-50 border border-uzima-maroon-200 rounded-xl p-5 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">About this assessment:</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{totalQuestions} evidence-based questions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Takes approximately 3-5 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Completely confidential and anonymous</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Instant personalized results and recommendations</span>
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Important Disclaimer:</p>
                  <p>
                    This screening tool is not a diagnostic instrument. It provides general information
                    about mental health symptoms but cannot replace professional evaluation. If you're
                    experiencing distress, please consult a qualified mental health professional.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep('personal')}
              className="w-full bg-gradient-uzima-primary text-white font-bold py-4 rounded-xl hover:shadow-2xl transition-all flex items-center justify-center gap-2"
            >
              Begin Assessment
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Personal Information Screen
  if (step === 'personal') {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/uzimabanner.jpg"
            alt="Personalized mental health journey"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-uzima-maroon-900/75 via-uzima-maroon-800/70 to-uzima-white-800/75"></div>
        </div>

        <div className="relative z-10 min-h-screen py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setStep('intro')}
              className="flex items-center gap-2 text-white/90 hover:text-white mb-6 font-medium transition-colors bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20"
            >
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
              Your Information
            </h2>
            <p className="text-gray-600 mb-8">
              This information is required to help us provide support if you need it.
              Your data remains completely confidential.
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={personalInfo.name}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={personalInfo.email}
                  onChange={handlePersonalInfoChange}
                  placeholder="your.email@example.com"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required for counselor follow-up if needed
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Age Range <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <select
                  name="age"
                  value={personalInfo.age}
                  onChange={handlePersonalInfoChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                >
                  <option value="">Select age range</option>
                  <option value="under-18">Under 18</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55+">55+</option>
                </select>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep('questions')}
                disabled={!personalInfo.name || !personalInfo.email}
                className="flex-1 bg-gradient-uzima-primary text-white font-bold py-3 rounded-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            {(!personalInfo.name || !personalInfo.email) && (
              <p className="text-sm text-red-600 text-center mt-2">
                Please fill in all required fields to continue
              </p>
            )}
          </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Questions Screen
  if (step === 'questions') {
    const question = assessment.questions[currentQuestion];
    const isAnswered = answers[question.id] !== undefined;

    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/uzimabanner.jpg"
            alt="Focus and reflection"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/85 via-uzima-maroon-50/90 to-white/85"></div>
        </div>

        <div className="relative z-10 min-h-screen py-12 px-4">
          <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Question {currentQuestion + 1} of {totalQuestions}
              </span>
              <span className="text-sm font-semibold text-uzima-maroon-600">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="h-full bg-gradient-uzima-primary"
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20"
            >
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-8">
                {question.text}
              </h2>

              <div className="space-y-3 mb-8">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(question.id, option.score)}
                    className={`w-full text-left p-5 border-2 rounded-xl transition-all ${
                      answers[question.id] === option.score
                        ? 'border-uzima-maroon-600 bg-gradient-to-r from-uzima-maroon-50 to-white shadow-md'
                        : 'border-gray-300 hover:border-uzima-maroon-300 hover:shadow'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{option.label}</span>
                      {answers[question.id] === option.score && (
                        <CheckCircle className="w-5 h-5 text-uzima-maroon-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:border-gray-400 hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={!isAnswered}
                  className="flex-1 bg-gradient-uzima-primary text-white font-bold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {currentQuestion === totalQuestions - 1 ? 'View Results' : 'Next Question'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (step === 'results') {
    const result = getResultInterpretation();
    const greeting = personalInfo.name ? `${personalInfo.name}, here are` : 'Here are';

    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/uzimabanner.jpg"
            alt="Mental health support and guidance"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-uzima-maroon-900/75 via-uzima-maroon-800/70 to-uzima-white-800/75"></div>
        </div>

        <div className="relative z-10 min-h-screen py-12 px-6">
          <div className="w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-3 gap-6"
          >
            {/* Left Section - Score Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/20 sticky top-6">
                <div className="text-center mb-4">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-${result.color}-500 to-${result.color}-600 flex items-center justify-center mx-auto mb-4`}>
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">
                    Assessment Complete
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {greeting} your results
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide mb-1">
                      Your Score
                    </p>
                    <p className="text-4xl font-display font-bold text-gray-900 mb-2">
                      {score} / {assessment.maxScore}
                    </p>
                    <p className={`inline-block px-3 py-1 rounded-full bg-${result.color}-100 text-${result.color}-800 font-bold text-sm`}>
                      {result.level}
                    </p>
                  </div>
                </div>

                {/* Mini CTA */}
                <div className="space-y-3">
                  <Link
                    to="/register"
                    className="flex items-center justify-center gap-2 w-full bg-gradient-uzima-primary text-white font-semibold px-4 py-2.5 rounded-lg hover:shadow-lg transition-all text-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    Book Session
                  </Link>
                  <a
                    href="tel:0800720648"
                    className="flex items-center justify-center gap-2 w-full bg-red-600 text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-red-700 transition-all text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Crisis Line
                  </a>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full text-white/90 hover:text-white font-medium transition-colors bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-lg text-sm"
                  >
                    Return Home
                  </button>
                </div>
              </div>
            </div>

            {/* Right Section - Detailed Results */}
            <div className="lg:col-span-2 space-y-6">
              <div className={`bg-white/95 backdrop-blur-md border-2 border-white/20 rounded-2xl p-6 shadow-2xl`}>
                <h3 className={`font-display font-bold text-gray-900 text-xl mb-3`}>
                  Understanding Your Results
                </h3>
                <p className={`text-gray-700 leading-relaxed`}>
                  {result.interpretation}
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">
                <h3 className="font-display font-bold text-gray-900 text-xl mb-4">
                  Recommended Next Steps
                </h3>
                <ul className="space-y-3">
                  {result.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <ChevronRight className="w-5 h-5 flex-shrink-0 mt-0.5 text-uzima-maroon-600" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
          </div>
        </div>

        {/* Urgent Booking Modal for High-Risk Cases */}
        {showUrgentBooking && (
          <UrgentBookingModal
            isOpen={showUrgentBooking}
            onClose={() => setShowUrgentBooking(false)}
            studentInfo={personalInfo}
            assessmentResult={assessmentResult}
          />
        )}
      </div>
    );
  }
};

export default AssessmentPage;
